import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../lib/AuthContext'
import { updateProfile, withdrawCapsule, supabase, getFollowCounts, uploadProfileImage, updateCapsuleContent } from '../lib/supabase'
import { useToast } from '../lib/useToast'
import NotificationButton from '../components/NotificationButton'
import DirectMessagesButton from '../components/DirectMessagesButton'
import './Profile.css'

const INTERESTS = ['Philosophy', 'Economics', 'Politics', 'Science', 'Culture', 'Technology', 'History', 'Ethics']

export default function Profile() {
  const { user, profile, refreshProfile } = useAuth()
  const navigate = useNavigate()
  const { toast, showToast } = useToast()

  const [displayName, setDisplayName] = useState('')
  const [bio, setBio] = useState('')
  const [interests, setInterests] = useState([])
  const [institution, setInstitution] = useState('')
  const [fieldOfStudy, setFieldOfStudy] = useState('')
  const [externalWebsite, setExternalWebsite] = useState('')
  const [loading, setLoading] = useState(false)
  const [avatarFile, setAvatarFile] = useState(null)
  const [avatarPreview, setAvatarPreview] = useState('')
  const [avatarError, setAvatarError] = useState('')
  const [avatarRemoved, setAvatarRemoved] = useState(false)
  const avatarInputRef = useRef(null)

  const [capsuleTab, setCapsuleTab] = useState('published') // 'published' | 'incubating'
  const [publishedCapsules, setPublishedCapsules] = useState([])
  const [pendingCapsules, setPendingCapsules] = useState([])
  const [loadingCapsules, setLoadingCapsules] = useState(true)
  const [followCounts, setFollowCounts] = useState({ followers: 0, following: 0 })

  // Withdraw modal
  const [withdrawTarget, setWithdrawTarget] = useState(null)
  const [withdrawing, setWithdrawing] = useState(false)

  useEffect(() => {
    if (!user) { navigate('/'); return }
    if (profile) {
      setDisplayName(profile.display_name || '')
      setBio(profile.bio || '')
      setInterests(profile.interests || [])
      setInstitution(profile.institution || '')
      setFieldOfStudy(profile.field_of_study || '')
      setExternalWebsite(profile.external_website || '')
      setAvatarPreview(profile.avatar_url || '')
      setAvatarFile(null)
      setAvatarError('')
      setAvatarRemoved(false)
    }
    loadMyCapsules()
    loadMyFollowCounts()
  }, [user, profile])

  const loadMyCapsules = async () => {
    if (!user) return
    setLoadingCapsules(true)
    try {
      const [publishedRes, pendingRes] = await Promise.all([
        supabase
          .from('capsules')
          .select('*')
          .eq('author_id', user.id)
          .eq('is_published', true)
          .order('publishes_at', { ascending: false })
          .limit(50),
        supabase
          .from('capsules')
          .select('*')
          .eq('author_id', user.id)
          .eq('is_published', false)
          .gt('publishes_at', new Date().toISOString())
          .order('publishes_at', { ascending: true })
      ])
      setPublishedCapsules(publishedRes.data || [])
      setPendingCapsules(pendingRes.data || [])
    } catch (e) {
      console.error(e)
    } finally {
      setLoadingCapsules(false)
    }
  }

  const loadMyFollowCounts = async () => {
    if (!user) return
    try {
      const counts = await getFollowCounts(user.id)
      setFollowCounts(counts)
    } catch (e) {
      console.error('Failed to load follow counts:', e)
    }
  }

  const toggleInterest = (interest) => {
    setInterests(prev =>
      prev.includes(interest) ? prev.filter(i => i !== interest) : [...prev, interest]
    )
  }

  const handleSave = async () => {
    setLoading(true)
    try {
      let avatarUrl = profile?.avatar_url || null
      if (avatarRemoved) {
        avatarUrl = null
      }
      if (avatarFile) {
        avatarUrl = await uploadProfileImage(avatarFile, user.id)
      }
      await updateProfile(user.id, {
        display_name: displayName,
        bio,
        interests,
        avatar_url: avatarUrl,
        institution: institution || null,
        field_of_study: fieldOfStudy || null,
        external_website: externalWebsite || null
      })
      refreshProfile()
      setAvatarFile(null)
      setAvatarRemoved(false)
      showToast('Profile updated.')
    } catch (e) {
      showToast('Error: ' + e.message, 'error')
    } finally {
      setLoading(false)
    }
  }

  const confirmWithdraw = async () => {
    if (!withdrawTarget) return
    setWithdrawing(true)
    try {
      await withdrawCapsule(withdrawTarget.id, user.id)
      setPendingCapsules(prev => prev.filter(c => c.id !== withdrawTarget.id))
      showToast('Capsule withdrawn and deleted.')
    } catch (e) {
      showToast('Could not withdraw: ' + e.message, 'error')
    } finally {
      setWithdrawTarget(null)
      setWithdrawing(false)
    }
  }

  const updatePendingCapsule = async (capsuleId, newContent) => {
    if (!user) throw new Error('You must be signed in.')

    const data = await updateCapsuleContent(capsuleId, user.id, newContent)

    setPendingCapsules(prev => prev.map(c => (c.id === capsuleId ? { ...c, ...data } : c)))
    showToast('Capsule updated. Publish timer was not reset.')
    return data
  }

  const getInitials = (name) => name?.split(' ').map(w => w[0]).join('').slice(0,2).toUpperCase() || '?'

  const handleAvatarSelect = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp']
    const maxBytes = 2 * 1024 * 1024 // 2 MB
    if (!allowedTypes.includes(file.type)) {
      setAvatarError('Profile photo must be JPG, PNG, or WebP.')
      return
    }
    if (file.size > maxBytes) {
      setAvatarError('Profile photo must be 2 MB or less.')
      return
    }

    const imageUrl = URL.createObjectURL(file)
    const dims = await new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => resolve({ width: img.width, height: img.height })
      img.onerror = reject
      img.src = imageUrl
    }).catch(() => null)

    if (!dims) {
      URL.revokeObjectURL(imageUrl)
      setAvatarError('Could not read image file.')
      return
    }

    const isSquare = Math.abs(dims.width - dims.height) <= 2
    const minPx = 256
    if (!isSquare || dims.width < minPx || dims.height < minPx) {
      URL.revokeObjectURL(imageUrl)
      setAvatarError('Use a square image at least 256 × 256 px.')
      return
    }

    setAvatarError('')
    setAvatarFile(file)
    setAvatarRemoved(false)
    setAvatarPreview(imageUrl)
  }

  return (
    <div className="profile-page">
      <header className="profile-header">
        <button className="back-btn" onClick={() => navigate('/feed')}>← Back to feed</button>
        <span className="profile-header-title">Profile</span>
        <div className="profile-header-right">
          <button
            className="back-btn"
            style={{ fontSize: '0.72rem' }}
            onClick={() => navigate('/civic-preferences')}
          >
            ◎ Civic
          </button>
          <button
            className="back-btn"
            style={{ fontSize: '0.72rem' }}
            onClick={() => navigate('/admin')}
          >
            Admin
          </button>
          <DirectMessagesButton />
          <NotificationButton userId={user?.id} />
        </div>
      </header>

      <div className="profile-layout">
        {/* Left — Edit card + stats */}
        <div className="profile-edit">
          <div className="profile-card">
            <div className="profile-card-header">
              <div
                className="profile-big-avatar"
                style={{ background: profile?.avatar_color || '#3a3028' }}
              >
                {avatarPreview ? (
                  <img src={avatarPreview} alt="" className="profile-avatar-image" />
                ) : (
                  getInitials(profile?.display_name)
                )}
              </div>
              <div>
                <div className="profile-username">@{profile?.username}</div>
                <div className="profile-depth-score">
                  Depth score: <span>{profile?.depth_score || 0}</span>
                </div>
              </div>
            </div>

            <div className="field">
              <label className="field-label">Profile photo <span style={{ color: 'var(--text-dim)' }}>(square, min 256×256, max 2MB)</span></label>
              <div className="profile-photo-controls">
                <input
                  ref={avatarInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  style={{ display: 'none' }}
                  onChange={handleAvatarSelect}
                />
                <button type="button" className="btn btn-ghost" onClick={() => avatarInputRef.current?.click()}>
                  {avatarPreview ? 'Change Photo' : 'Upload Photo'}
                </button>
                {avatarPreview && (
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => {
                      setAvatarPreview('')
                      setAvatarFile(null)
                      setAvatarError('')
                      setAvatarRemoved(true)
                    }}
                  >
                    Remove
                  </button>
                )}
              </div>
              {avatarError && <div className="pending-capsule-edit-error" style={{ marginTop: '0.5rem' }}>{avatarError}</div>}
            </div>

            <div className="field">
              <label className="field-label">Display name</label>
              <input
                className="field-input"
                value={displayName}
                onChange={e => setDisplayName(e.target.value)}
              />
            </div>

            <div className="field">
              <label className="field-label">Bio</label>
              <textarea
                className="field-input field-textarea"
                value={bio}
                onChange={e => setBio(e.target.value)}
                rows={3}
                maxLength={280}
                placeholder="What do you think about?"
              />
            </div>

            <div className="field">
              <label className="field-label">Interests <span style={{ color: 'var(--text-dim)' }}>(shapes your feed)</span></label>
              <div className="interests-grid" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: '0.4rem' }}>
                {INTERESTS.map(interest => (
                  <button
                    key={interest}
                    type="button"
                    className={`interest-btn ${interests.includes(interest) ? 'selected' : ''}`}
                    onClick={() => toggleInterest(interest)}
                  >
                    {interest}
                  </button>
                ))}
              </div>
            </div>

            {/* Credibility fields */}
            <div className="profile-credibility-section">
              <div className="profile-credibility-title">Academic Credibility <span style={{ color: 'var(--text-dim)' }}>(optional)</span></div>

              <div className="field">
                <label className="field-label">🏫 Institution</label>
                <input
                  className="field-input"
                  value={institution}
                  onChange={e => setInstitution(e.target.value)}
                  placeholder="e.g., Harvard University"
                  maxLength={100}
                />
              </div>

              <div className="field">
                <label className="field-label">🎓 Field of study</label>
                <input
                  className="field-input"
                  value={fieldOfStudy}
                  onChange={e => setFieldOfStudy(e.target.value)}
                  placeholder="e.g., Philosophy, Economics"
                  maxLength={100}
                />
              </div>

              <div className="field">
                <label className="field-label">🔗 Website</label>
                <input
                  className="field-input"
                  type="url"
                  value={externalWebsite}
                  onChange={e => setExternalWebsite(e.target.value)}
                  placeholder="https://example.com or Substack, Google Scholar"
                  maxLength={200}
                />
              </div>
            </div>

            <button className="btn btn-primary" onClick={handleSave} disabled={loading} style={{ marginTop: '0.5rem' }}>
              {loading ? <span className="spinner" /> : 'Save Changes'}
            </button>
          </div>

          {/* Stats */}
          <div className="profile-stats">
            <div className="stat-item">
              <div className="stat-value">{profile?.capsule_count || 0}</div>
              <div className="stat-label">Capsules</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{profile?.response_count || 0}</div>
              <div className="stat-label">Responses</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{profile?.depth_score || 0}</div>
              <div className="stat-label">Depth score</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{followCounts.followers || 0}</div>
              <div className="stat-label">Followers</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{followCounts.following || 0}</div>
              <div className="stat-label">Following</div>
            </div>
          </div>
        </div>

        {/* Right — Capsule tabs */}
        <div className="profile-capsules">
          <div className="profile-capsule-tabs">
            <button
              className={`profile-capsule-tab ${capsuleTab === 'published' ? 'active' : ''}`}
              onClick={() => setCapsuleTab('published')}
            >
              Published
              {publishedCapsules.length > 0 && (
                <span className="profile-tab-count">{publishedCapsules.length}</span>
              )}
            </button>
            <button
              className={`profile-capsule-tab ${capsuleTab === 'incubating' ? 'active' : ''}`}
              onClick={() => setCapsuleTab('incubating')}
            >
              In Incubation
              {pendingCapsules.length > 0 && (
                <span className="profile-tab-count profile-tab-count--pending">{pendingCapsules.length}</span>
              )}
            </button>
          </div>

          {loadingCapsules ? (
            <div className="profile-capsules-loading"><span className="spinner" /></div>
          ) : (
            <>
              {/* PUBLISHED */}
              {capsuleTab === 'published' && (
                <>
                  {publishedCapsules.length === 0 ? (
                    <div className="profile-capsules-empty">No published capsules yet.</div>
                  ) : (
                    publishedCapsules.map(c => (
                      <div
                        key={c.id}
                        className="my-capsule-item my-capsule-item--clickable"
                        role="button"
                        tabIndex={0}
                        onClick={() => navigate(`/capsule/${c.id}?view=responses`)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            navigate(`/capsule/${c.id}?view=responses`)
                          }
                        }}
                      >
                        <div className="my-capsule-body">{c.content}</div>
                        <div className="my-capsule-meta">
                          {new Date(c.publishes_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          {c.tags?.length > 0 && (
                            <span style={{ marginLeft: '0.75rem' }}>
                              {c.tags.map(t => <span key={t} className="tag" style={{ marginRight: '0.3rem' }}>{t}</span>)}
                            </span>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </>
              )}

              {/* IN INCUBATION */}
              {capsuleTab === 'incubating' && (
                <>
                  <p className="profile-incubation-microcopy">
                    Posts in incubation are visible with a countdown. You can revise yours before they publish.
                  </p>
                  {pendingCapsules.length === 0 ? (
                    <div className="profile-capsules-empty">Nothing in incubation right now.</div>
                  ) : (
                    pendingCapsules.map(c => (
                      <PendingProfileCapsule
                        key={c.id}
                        capsule={c}
                        onEdit={updatePendingCapsule}
                        onWithdraw={() => setWithdrawTarget(c)}
                      />
                    ))
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Withdraw modal */}
      {withdrawTarget && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setWithdrawTarget(null)}>
          <div className="modal">
            <div className="modal-title">Withdraw this capsule?</div>
            <div className="modal-body">
              This capsule is still in incubation and has not been published. Withdrawing it will permanently delete it. This action cannot be undone.
            </div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setWithdrawTarget(null)} disabled={withdrawing}>Keep it</button>
              <button className="btn btn-danger" onClick={confirmWithdraw} disabled={withdrawing}>
                {withdrawing ? <span className="spinner" /> : 'Withdraw'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={`toast ${toast.visible ? 'show' : ''} ${toast.type}`}>{toast.message}</div>
    </div>
  )
}

// ── Pending capsule item with live countdown ──────────────────────────────────

function PendingProfileCapsule({ capsule, onWithdraw, onEdit }) {
  const [timeLeft, setTimeLeft] = useState('')
  const [editing, setEditing] = useState(false)
  const [editContent, setEditContent] = useState(capsule.content)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    setEditContent(capsule.content)
  }, [capsule.content])

  useEffect(() => {
    const update = () => {
      const diff = new Date(capsule.publishes_at) - new Date()
      if (diff <= 0) { setTimeLeft('Publishing…'); return }
      const h = Math.floor(diff / 3600000)
      const m = Math.floor((diff % 3600000) / 60000)
      const s = Math.floor((diff % 60000) / 1000)
      setTimeLeft(`${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`)
    }
    update()
    const interval = setInterval(update, 1000)
    return () => clearInterval(interval)
  }, [capsule.publishes_at])

  return (
    <div className="my-capsule-item my-capsule-item--pending">
      <div className="my-capsule-incubation-badge">
        <div className="incubation-dot" />
        Publishes in <span className="incubation-countdown">{timeLeft}</span>
      </div>
      {editing ? (
        <div className="pending-capsule-edit">
          <textarea
            className="pending-capsule-edit-input"
            value={editContent}
            onChange={e => setEditContent(e.target.value)}
            rows={5}
            maxLength={10000}
            autoFocus
          />
          {error && <div className="pending-capsule-edit-error">{error}</div>}
          <div className="pending-capsule-edit-actions">
            <button
              className="btn btn-ghost"
              onClick={() => { setEditing(false); setEditContent(capsule.content); setError('') }}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={async () => {
                if (!editContent.trim()) return
                setSaving(true)
                setError('')
                try {
                  await onEdit(capsule.id, editContent.trim())
                  setEditing(false)
                } catch (e) {
                  setError(e.message || 'Failed to update capsule')
                } finally {
                  setSaving(false)
                }
              }}
              disabled={saving || editContent.trim().length < 50}
            >
              {saving ? <span className="spinner" /> : 'Save Edit'}
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="my-capsule-body">{capsule.content}</div>
          <div className="my-capsule-meta my-capsule-meta--pending">
            {capsule.tags?.length > 0 && (
              <span>
                {capsule.tags.map(t => <span key={t} className="tag" style={{ marginRight: '0.3rem' }}>{t}</span>)}
              </span>
            )}
            <div className="my-capsule-pending-actions">
              <button className="btn btn-ghost my-capsule-edit-btn" onClick={() => setEditing(true)}>
                Edit
              </button>
              <button className="btn btn-danger my-capsule-withdraw-btn" onClick={onWithdraw}>
                Withdraw
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
